<script setup lang="ts">
import CheckboxComponent from "@/stories/form/Checkbox/CheckboxComponent.vue";
import { onBeforeUnmount, onMounted, ref, toRefs } from "vue";
import imageLoading from "@/assets/img/image-loading.gif";
import { sentryException } from "@/sentry";

interface Props {
  src: string;
  alt: string;
  checked?: boolean;
  disabled?: boolean;
  lazyLoading?: boolean;
  lazyLoadingSelector?: string;
}

const props = withDefaults(defineProps<Props>(), {
  checked: false,
  lazyLoading: false,
});

defineEmits<{
  toggle: [];
}>();

const imgEl = ref<HTMLImageElement | null>(null);
let observer: IntersectionObserver | null = null;

const { src, lazyLoading, lazyLoadingSelector } = toRefs(props);

onMounted(() => {
  if (lazyLoading.value) {
    observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        const entry = entries[0];
        if (entry?.isIntersecting && imgEl.value) {
          imgEl.value.src = src.value;
          observer?.disconnect();
        }
      },
      {
        root: document.querySelector(`.${lazyLoadingSelector.value}`),
      },
    );

    if (imgEl.value) {
      observer.observe(imgEl.value);
    }
  }
});

onBeforeUnmount(() => {
  observer?.disconnect();
});

const handleError = () => {
  sentryException(new Error("PixabayError: Too Many Requests"));
};
</script>

<template>
  <button
    type="button"
    :class="[
      'w-full rounded-md overflow-hidden relative max-h-[8.375rem] h-[8.375rem] min-h-[8.375rem]',
      'focus-visible-strong focus-visible:outline-offset-1',
      checked && 'outline outline-2 outline-neutral1000',
    ]"
    :disabled="disabled && !checked"
    @click="$emit('toggle')"
  >
    <img
      ref="imgEl"
      class="h-[8.375rem] min-h-[8.375rem] max-h-[8.375rem] w-full object-cover rounded-md border border-neutral300"
      :class="{ 'opacity-50': disabled && !checked }"
      :alt="alt"
      :src="lazyLoading ? imageLoading : src"
      @error="handleError"
    />

    <CheckboxComponent
      v-if="checked"
      checked
      class="absolute right-2 bottom-2"
      :tabindex="-1"
      :size="'lg'"
    />
  </button>
</template>
